import { useEffect, useRef, useState } from 'react';
import { authService } from '../../services/auth';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitConfig) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
          prompt: (cb?: (n: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
          cancel: () => void;
        };
      };
    };
  }
}

interface GoogleInitConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  use_fedcm_for_prompt?: boolean;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string | number;
  locale?: string;
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let scriptPromise: Promise<void> | null = null;
function loadGisScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${GIS_SRC}"]`,
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Identity library')),
      );
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity library'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface GoogleSignInButtonProps {
  /** Called with the Google ID token when the user signs in. */
  onCredential: (credential: string) => void | Promise<void>;
  /** Called whenever the integration fails (script, client id, or callback). */
  onError?: (error: Error) => void;
  text?: GoogleButtonOptions['text'];
  theme?: GoogleButtonOptions['theme'];
  width?: number;
  className?: string;
}

/**
 * Renders the official Google Identity Services button.
 * Uses the supported renderButton() flow (FedCM-compatible) instead of
 * the legacy "render-into-hidden-div + synthetic-click" hack, which silently
 * fails in modern Chrome.
 */
export function GoogleSignInButton({
  onCredential,
  onError,
  text = 'signin_with',
  theme = 'outline',
  width = 320,
  className,
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        await loadGisScript();
        if (cancelled) return;

        const clientId = await authService.getGoogleClientId();
        if (cancelled) return;
        if (!clientId) throw new Error('Google Client ID is not available');
        if (!window.google?.accounts?.id) {
          throw new Error('Google Identity library failed to initialize');
        }

        window.google.accounts.id.initialize({
          client_id: clientId,
          use_fedcm_for_prompt: true,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: (response) => {
            if (!response?.credential) {
              const e = new Error('No credential received from Google');
              setErrorMsg(e.message);
              onError?.(e);
              return;
            }
            try {
              void onCredential(response.credential);
            } catch (e) {
              const err = e instanceof Error ? e : new Error('Google sign-in failed');
              setErrorMsg(err.message);
              onError?.(err);
            }
          },
        });

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          // GIS renders a fixed-width iframe; clamp to the container so it
          // never overflows narrow phone cards (GIS minimum is 200px).
          const available = containerRef.current.offsetWidth;
          const buttonWidth = available > 0 ? Math.max(200, Math.min(width, available)) : width;
          window.google.accounts.id.renderButton(containerRef.current, {
            type: 'standard',
            theme,
            size: 'large',
            text,
            shape: 'rectangular',
            logo_alignment: 'left',
            width: buttonWidth,
            locale: 'en',
          });
        }

        setStatus('ready');
      } catch (e) {
        const err = e instanceof Error ? e : new Error('Google sign-in initialization failed');
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err.message);
        onError?.(err);
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, text, width]);

  return (
    <div className={className}>
      <div
        ref={containerRef}
        aria-label="Sign in with Google"
        className="flex min-h-[44px] w-full items-center justify-center"
      />
      {status === 'loading' && (
        <p className="mt-2 text-center text-xs text-stitch-muted">Loading Google sign-in…</p>
      )}
      {status === 'error' && errorMsg && (
        <p className="mt-2 text-center text-xs text-stitch-danger" role="alert">
          {errorMsg}
        </p>
      )}
    </div>
  );
}
